import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ebookStore } from '../store/ebookStore'
import SectionRenderer from './SectionRenderer'

const ChapterView = ({ chapter, chapterIndex, totalChapters, onNavigate }) => {
  const [currentSection, setCurrentSection] = useState(0)
  const [completedSections, setCompletedSections] = useState([])
  
  const { updateReadingProgress, addNote, getNote } = ebookStore()
  const sections = chapter?.sections || []

  useEffect(() => {
    setCurrentSection(0)
    setCompletedSections([])
  }, [chapter.id])

  useEffect(() => {
    if (sections.length > 0) {
      const progress = Math.round((currentSection / sections.length) * 100)
      updateReadingProgress(chapter.id, progress)
    }
  }, [currentSection, sections.length, chapter.id])

  const handleSectionComplete = () => {
    const sectionId = sections[currentSection]?.id
    if (sectionId && !completedSections.includes(sectionId)) {
      setCompletedSections([...completedSections, sectionId])
    }
    if (currentSection < sections.length - 1) {
      setCurrentSection(prev => prev + 1)
    }
  }

  const goToSection = (index) => {
    setCurrentSection(index)
  }

  if (!chapter || sections.length === 0) {
    return (
      <div className="chapter-container">
        <p>Cargando capítulo...</p>
      </div>
    )
  }

  return (
    <div className="chapter-container">
      <div className="chapter-header">
        <span className="chapter-label">
          {chapter.bonus ? 'BÓNUS' : 'Capítulo'} {chapterIndex + 1}
        </span>
        <h2 className="chapter-title">{chapter.title}</h2>
        {chapter.subtitle && (
          <p className="chapter-subtitle">{chapter.subtitle}</p>
        )}
        <div className="section-progress">
          <span>{currentSection + 1} / {sections.length}</span>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="sections-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${chapter.id}-${currentSection}`}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            <SectionRenderer 
              section={sections[currentSection]}
              chapterId={chapter.id}
              onComplete={handleSectionComplete}
              addNote={addNote}
              getNote={getNote}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="chapter-navigation">
        <button 
          className="nav-button prev"
          onClick={() => {
            if (currentSection > 0) {
              setCurrentSection(prev => prev - 1)
            } else {
              onNavigate('prev')
            }
          }}
          disabled={chapterIndex === 0 && currentSection === 0}
        >
          ← Anterior
        </button>
        
        <div className="section-dots">
          {sections.map((section, i) => (
            <button
              key={i}
              className={`dot ${i === currentSection ? 'active' : ''} ${completedSections.includes(section.id) ? 'completed' : ''}`}
              onClick={() => goToSection(i)}
              title={`Sección ${i + 1}`}
            />
          ))}
        </div>

        <button 
          className="nav-button next"
          onClick={() => {
            if (currentSection < sections.length - 1) {
              setCurrentSection(prev => prev + 1)
            } else {
              onNavigate('next')
            }
          }}
        >
          {currentSection < sections.length - 1 ? 'Siguiente →' : 'Siguiente capítulo →'}
        </button>
      </div>
    </div>
  )
}

export default ChapterView
